import { Container, Grid, GridItem, VStack, useDisclosure } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { LDFlags } from '../../../../../constants/flags';
import { MARKETPLACE_PHOTOS } from '../../../../../constants/urls';
import { DealMediaTypeEnum } from '../../../../../gql/generated/graphql';
import { useFlag } from '../../../../../hooks';
import useEffectiveDealFields from '../../../hooks/useEffectiveDealFields';
import { UserDeal } from '../../../utils';
import MarketplaceErrorState from '../MarketplaceErrorState';
import DelistFooter from '../delist/DelistFooter';
import { isParkedPath, shouldShowGate, shouldShowKeepTheCarCard } from '../guidance';
import useLeadGuidance from '../hooks/useLeadGuidance';
import usePayoffDisplay from '../hooks/usePayoffDisplay';
import { PRELIST_PHOTO_SLOT_ID } from '../photos/constants';
import MarketplacePrelistLoadingSkeleton from '../prelist/MarketplacePrelistLoadingSkeleton';
import useMarketplaceDashboardState from '../useMarketplaceDashboardState';
import AlmostLivePanel from './AlmostLivePanel';
import MarketplaceAccepted from './MarketplaceAccepted';
import MarketplaceEnded from './MarketplaceEnded';
import MarketplaceLiveHeader, { HeaderIndicator } from './MarketplaceLiveHeader';
import MarketplaceLivePhotosCard from './MarketplaceLivePhotosCard';
import MarketplaceOffers from './MarketplaceOffers';
import MarketplaceSold from './MarketplaceSold';
import SellerReviewsCard from './SellerReviewsCard';
import { useMarkSeen } from './conversation/useMarkSeen';
import { getUnseenNotelessOfferIds } from './conversation/utils';
import GateScreen from './gate/GateScreen';
import { hasNonDriverAskOffer, hasVisibleOfferCards } from './offers/utils';
import ParkedPathPanel from './parked/ParkedPathPanel';
import ParkedWayBackCards from './parked/ParkedWayBackCards';
import FinishYourSaleHub from './postAcceptance/FinishYourSaleHub';
import SoldReveal from './postAcceptance/SoldReveal';
import UploadFileModal from './postAcceptance/documents/UploadFileModal';
import {
  isLivePostAcceptanceStatus,
  isPostAcceptanceLead,
} from './postAcceptance/postAcceptanceStatus';
import { useDocumentUpload } from './postAcceptance/useDocumentUpload';
import KeepTheCarCard from './waiting/KeepTheCarCard';

type MarketplaceLiveDashboardProps = {
  userDeal: UserDeal;
};

const TWO_COL_GRID_PROPS = {
  templateColumns: { base: 'minmax(0, 1fr)', md: 'minmax(0, 1.4fr) minmax(0, 1fr)' },
  gap: { base: 4, md: 5 },
  alignItems: 'stretch',
} as const;

const PayoffUploadModal = ({
  dealId,
  lienholderName,
  onClose,
}: {
  dealId: string;
  lienholderName: string | null | undefined;
  onClose: () => void;
}) => {
  const { upload, isProcessing } = useDocumentUpload(dealId, { enabled: false });
  return (
    <UploadFileModal
      isOpen
      onClose={onClose}
      upload={upload}
      isProcessing={isProcessing}
      extraDocumentOptions={[
        {
          kind: 'document',
          key: 'payoff',
          label: 'Payoff document',
          dealOnlyMediaType: DealMediaTypeEnum.PayoffDocs,
          hint: `A payoff quote or recent statement from ${
            lienholderName ?? 'your lienholder'
          } works. It should show the full amount to pay off your lease.`,
        },
      ]}
    />
  );
};

const MarketplaceLiveDashboard = ({ userDeal }: MarketplaceLiveDashboardProps) => {
  const dealId = userDeal?.id || '';
  const history = useHistory();

  const { externalId, lead, photos, marketplaceState, isLoading, error, refetch } =
    useMarketplaceDashboardState(dealId);
  const { listingStatus } = marketplaceState;
  const sellerConfidenceEnabled = useFlag(LDFlags.MARKETPLACE_SELLER_CONFIDENCE);

  const guidance = useLeadGuidance(lead);

  const parked =
    listingStatus === 'parked' && guidance != null && isParkedPath(guidance.gateDecision)
      ? { guidance, path: guidance.gateDecision }
      : null;

  const { cameFromMarketplace, isHandedBack } = useEffectiveDealFields(userDeal);
  const canRelist = !cameFromMarketplace || isHandedBack;

  const headerIndicator: HeaderIndicator =
    parked == null
      ? listingStatus
      : parked.path === 'buyout'
      ? 'parkedBuyout'
      : 'parkedFinishLease';

  const payoffDisplay = usePayoffDisplay(userDeal, lead, {
    enabled:
      sellerConfidenceEnabled && (listingStatus === 'active' || listingStatus === 'almostLive'),
  });

  const offersEnabled = useFlag(LDFlags.MARKETPLACE_OFFERS_ENABLED);
  const postAcceptanceEnabled = useFlag(LDFlags.MARKETPLACE_POST_ACCEPTANCE_ENABLED);
  const messagingEnabled = useFlag(LDFlags.MARKETPLACE_MESSAGING);

  const uploadDocumentModal = useDisclosure();

  const effectiveOffers = lead?.offers;
  const hasVisibleOffers = hasVisibleOfferCards(effectiveOffers);
  const markSeen = useMarkSeen(externalId);
  useEffect(() => {
    if (!messagingEnabled) return;
    markSeen({ offers: getUnseenNotelessOfferIds(effectiveOffers) });
  }, [messagingEnabled, effectiveOffers, markSeen]);
  const showOffersFirst = offersEnabled && hasNonDriverAskOffer(effectiveOffers);

  // Offers arrive oldest-first from the server.
  const acceptedOffer = effectiveOffers
    ?.slice()
    .reverse()
    .find((offer) => offer.status === 'accepted');

  const revealEligible =
    postAcceptanceEnabled &&
    lead != null &&
    isLivePostAcceptanceStatus(lead.status) &&
    lead.driverEvents?.sold_reveal_seen == null &&
    acceptedOffer != null;

  const [showReveal, setShowReveal] = useState(false);

  const revealDismissed = useRef(false);
  useEffect(() => {
    if (revealEligible && !revealDismissed.current) {
      setShowReveal(true);
    }
  }, [revealEligible]);

  const shouldShowReveal = showReveal || (revealEligible && !revealDismissed.current);

  const handleFinishReveal = () => {
    revealDismissed.current = true;
    setShowReveal(false);
  };

  if (error && !lead) {
    return (
      <Container maxW="container.xl" my="20px" px={{ base: '16px', md: '24px' }}>
        <MarketplaceErrorState onRetry={refetch} />
      </Container>
    );
  }

  if (!userDeal || isLoading) {
    return <MarketplacePrelistLoadingSkeleton />;
  }

  const { photosUploaded } = marketplaceState.photos;
  const photoPreviews = [...photos]
    .sort(
      (a, b) =>
        Number(b.label === PRELIST_PHOTO_SLOT_ID) - Number(a.label === PRELIST_PHOTO_SLOT_ID),
    )
    .map((photo) => ({ id: photo.id, url: photo.url }));
  const vehicle = {
    year: lead?.year,
    make: lead?.make,
    model: lead?.model,
    mileage: lead?.mileage,
  };

  const handleManagePhotos = () => history.push(MARKETPLACE_PHOTOS);

  const showPostAcceptanceHub = postAcceptanceEnabled && lead && isPostAcceptanceLead(lead);

  const renderBody = () => {
    if (showPostAcceptanceHub) {
      if (shouldShowReveal) return null;
      return (
        <FinishYourSaleHub
          dealId={dealId}
          lead={lead}
          acceptedOffer={acceptedOffer}
          externalId={externalId}
          vehicle={vehicle}
        />
      );
    }

    if (listingStatus === 'almostLive' && guidance != null && shouldShowGate(guidance)) {
      return (
        <GateScreen
          guidance={guidance}
          externalId={externalId}
          dealId={dealId}
          leadId={lead?.id ?? ''}
          model={lead?.model}
        />
      );
    }

    if (parked != null) {
      return (
        <Grid {...TWO_COL_GRID_PROPS}>
          <GridItem>
            <ParkedPathPanel
              path={parked.path}
              guidance={parked.guidance}
              dealId={dealId}
              leadId={lead?.id ?? ''}
              model={lead?.model}
            />
          </GridItem>

          <GridItem minW={0}>
            <VStack align="stretch" spacing={{ base: 4, md: 5 }}>
              <ParkedWayBackCards
                path={parked.path}
                guidance={parked.guidance}
                externalId={externalId}
                dealId={dealId}
                leadId={lead?.id ?? ''}
                model={lead?.model}
                canRelist={canRelist}
              />
            </VStack>
          </GridItem>
        </Grid>
      );
    }

    if (listingStatus === 'almostLive') {
      return (
        <>
          <Grid {...TWO_COL_GRID_PROPS}>
            <GridItem>
              <AlmostLivePanel waitingSentence={payoffDisplay.sentence} />
            </GridItem>

            <GridItem minW={0}>
              <VStack align="stretch" spacing={{ base: 4, md: 5 }}>
                <MarketplaceLivePhotosCard
                  photosUploaded={photosUploaded}
                  photos={photoPreviews}
                  onAddPhotos={handleManagePhotos}
                  variant="almostLive"
                  onUploadDocument={uploadDocumentModal.onOpen}
                />
                <SellerReviewsCard />
              </VStack>
            </GridItem>
          </Grid>

          <DelistFooter
            externalId={externalId}
            offers={effectiveOffers}
            actionLabel="Cancel your listing"
          />
        </>
      );
    }

    if (offersEnabled && listingStatus === 'accepted') {
      return (
        <Grid {...TWO_COL_GRID_PROPS}>
          <GridItem>
            <MarketplaceAccepted
              amount={acceptedOffer?.amount}
              dealershipName={acceptedOffer?.orgName}
              vehicle={vehicle}
            />
          </GridItem>

          <GridItem minW={0}>
            <MarketplaceLivePhotosCard
              photosUploaded={photosUploaded}
              photos={photoPreviews}
              onAddPhotos={handleManagePhotos}
              variant="postAcceptance"
            />
          </GridItem>
        </Grid>
      );
    }

    if (listingStatus === 'sold') {
      return (
        <MarketplaceSold
          amount={acceptedOffer?.amount}
          dealershipName={acceptedOffer?.orgName}
          vehicle={vehicle}
        />
      );
    }

    if (listingStatus === 'ended') {
      return <MarketplaceEnded vehicle={vehicle} />;
    }

    return (
      <>
        <Grid {...TWO_COL_GRID_PROPS}>
          <GridItem order={{ base: showOffersFirst ? 1 : 2, md: 'unset' }}>
            <MarketplaceOffers
              offersEnabled={offersEnabled}
              offers={effectiveOffers}
              messages={lead?.messages}
              externalId={externalId}
              dealId={dealId}
              leadId={lead?.id ?? ''}
              listedAt={lead?.activatedAt ?? lead?.createdAt}
              payoffAmount={payoffDisplay.amount}
              guidance={guidance}
              model={lead?.model}
              hasVisibleOffers={hasVisibleOffers}
              payoffIsTrusted={payoffDisplay.showsTrustedNumber}
              lienholderSlug={lead?.lienholderSlug ?? null}
              lienholderName={lead?.lienholderName ?? null}
            />
          </GridItem>

          <GridItem order={{ base: showOffersFirst ? 2 : 1, md: 'unset' }} minW={0}>
            <VStack align="stretch" spacing={{ base: 4, md: 5 }}>
              <MarketplaceLivePhotosCard
                photosUploaded={photosUploaded}
                photos={photoPreviews}
                onAddPhotos={handleManagePhotos}
                variant={listingStatus === 'accepted' ? 'postAcceptance' : 'live'}
                onUploadDocument={uploadDocumentModal.onOpen}
              />
              {guidance != null &&
                shouldShowKeepTheCarCard(guidance.tier) &&
                offersEnabled &&
                hasVisibleOffers && (
                  <KeepTheCarCard model={lead?.model} dealId={dealId} leadId={lead?.id ?? ''} />
                )}
            </VStack>
          </GridItem>
        </Grid>

        <DelistFooter externalId={externalId} offers={effectiveOffers} />
      </>
    );
  };
  return (
    <>
      {shouldShowReveal && lead && acceptedOffer && (
        <SoldReveal
          dealId={dealId}
          lead={lead}
          acceptedOffer={acceptedOffer}
          onFinish={handleFinishReveal}
        />
      )}
      <Container maxW="container.xl" my="20px" px={{ base: '16px', md: '24px' }}>
        <VStack align="stretch" spacing={{ base: 4, md: 5 }}>
          {!showPostAcceptanceHub && (
            <MarketplaceLiveHeader
              vehicle={{ year: lead?.year, make: lead?.make, model: lead?.model }}
              vin={lead?.vin}
              indicator={headerIndicator}
              payoffDisplay={payoffDisplay}
            />
          )}

          {renderBody()}
        </VStack>
      </Container>
      {uploadDocumentModal.isOpen && (
        <PayoffUploadModal
          dealId={dealId}
          lienholderName={lead?.lienholderName}
          onClose={uploadDocumentModal.onClose}
        />
      )}
    </>
  );
};

export default MarketplaceLiveDashboard;
